/* eslint-disable react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */
import {
  Box,
  Button,
  useTheme,
  alpha,
  InputAdornment,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
  IconButton,
  Skeleton,
  Theme,
  SxProps,
} from '@mui/material';
import {
  OptionLegGreeks,
  PositionLeg,
  StrikeDeltaObject,
} from 'types/optionsCalculator';
import DeleteIcon from '@mui/icons-material/Delete';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import { useRecoilValue } from 'recoil';
import { negativeTrendColorState, positiveTrendColorState } from 'states';
import { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import debounce from 'lodash/debounce';
import { convertToDaysTillExpiry, getExpirationLabel } from 'util/iVol';
import { FixedSizeList as List } from 'react-window';
import {
  dayjs,
  formatAsCompactNumber,
  formatAsCurrency,
  formatAsPercentage,
} from 'util/shared';
import BuySellBtn from './BuySellBtn';
import { SGTooltip } from 'components/core';

interface EditableOptionLegProps {
  leg: PositionLeg;
  expirationOptions: number[];
  disabledExpirationOptions: number[];
  strikeDeltaOptions: StrikeDeltaObject[];
  legGreeks: OptionLegGreeks | null;
  greeksLoading?: boolean;
  onEditLeg: (
    legId: number,
    updatedLeg: Partial<PositionLeg>,
    shouldUseDefaultPrice?: boolean,
  ) => void;
  onDeleteLeg: (legId: number) => void;
}

interface StrikeItemProps {
  index: number;
  style: React.CSSProperties;
  data: {
    strikeDeltaOptions: StrikeDeltaObject[];
    selectedStrike: number | null;
    onStrikeSelect: (strike: number) => void;
    isCall: boolean;
    isBuy: boolean;
  };
}

interface GreekBoxProps {
  label: string;
  value: number;
  description: string;
  decimals?: number;
  format?: 'percent' | 'decimal';
  useColorCoding?: boolean;
  adjustedValue?: number;
  sx?: SxProps<Theme>;
}

const GreekBox = ({
  label,
  value,
  description,
  decimals = 3,
  format = 'decimal',
  useColorCoding = false,
  adjustedValue,
  sx,
}: GreekBoxProps) => {
  const theme = useTheme();
  const serverPosColor = useRecoilValue(positiveTrendColorState);
  const serverNegColor = useRecoilValue(negativeTrendColorState);

  const formatGreekValue = (
    value: number,
    decimals: number = 3,
    format: 'percent' | 'decimal' = 'decimal',
  ) => {
    if (format === 'percent') {
      return `${formatAsPercentage(value)}`;
    }
    return formatAsCompactNumber(value, { maximumFractionDigits: decimals });
  };

  const getGreekColor = (value: number) => {
    if (value > 0) {
      return serverPosColor;
    } else if (value < 0) {
      return serverNegColor;
    }
    return theme.palette.text.secondary;
  };

  const boxColor = useColorCoding
    ? getGreekColor(adjustedValue ?? value)
    : theme.palette.text.primary;

  return (
    <SGTooltip title={description}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          p: 1,
          borderRadius: 1.5,
          backgroundColor: alpha(boxColor, 0.08),
          border: `1px solid ${alpha(boxColor, 0.2)}`,
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          '&:hover': {
            backgroundColor: alpha(boxColor, 0.12),
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px ${alpha(boxColor, 0.15)}`,
          },
          minWidth: '105px',
          ...sx,
        }}
      >
        {adjustedValue && (
          <Box
            sx={{
              position: 'absolute',
              top: -6,
              right: -6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              backgroundColor: theme.palette.warning.main,
              borderRadius: '50%',
              border: `2px solid ${theme.palette.background.paper}`,
              boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.4)}`,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.6)}`,
              },
            }}
          >
            <PrecisionManufacturingIcon
              sx={{
                fontSize: 12,
                color: theme.palette.warning.contrastText,
              }}
            />
          </Box>
        )}
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
            fontSize: { xs: 10, md: 12 },
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {label}
        </Typography>
        {adjustedValue != null ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: alpha(theme.palette.text.secondary, 0.8),
                fontWeight: 500,
                fontSize: { xs: 8, md: 10 },
                textDecoration: 'line-through',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: '1px',
                  backgroundColor: theme.palette.text.secondary,
                  opacity: 0.7,
                },
              }}
            >
              {formatGreekValue(value, decimals, format)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: boxColor,
                fontWeight: 600,
                fontSize: { xs: 10, md: 12 },
              }}
            >
              {formatGreekValue(adjustedValue, decimals, format)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: boxColor,
                fontWeight: 500,
                fontSize: { xs: 8, md: 10 },
              }}
            >
              (Adj.)
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: boxColor,
              fontWeight: 600,
              fontSize: { xs: 10, md: 12 },
            }}
          >
            {formatGreekValue(value, decimals, format)}
          </Typography>
        )}
      </Box>
    </SGTooltip>
  );
};

const StrikeItem = ({ index, style, data }: StrikeItemProps) => {
  const { strikeDeltaOptions, selectedStrike, onStrikeSelect, isCall, isBuy } =
    data;
  const strikeDeltaObject = strikeDeltaOptions[index];
  const strike = strikeDeltaObject.strike;
  const delta = isCall
    ? strikeDeltaObject.callDelta
    : strikeDeltaObject.putDelta;
  const adjustedDelta = delta * (isBuy ? 1 : -1);
  return (
    <div key={strike} style={style}>
      <MenuItem
        value={strike || ''}
        onClick={() => onStrikeSelect(strike)}
        sx={{
          padding: '10px',
          width: '100%',
        }}
      >
        <Checkbox
          checked={selectedStrike === strike}
          style={{
            padding: '0px 10px 0px 0px',
          }}
        />
        <ListItemText
          primary={`${formatAsCurrency(strike)}`}
          secondary={`DELTA: ${formatAsCompactNumber(adjustedDelta, { maximumFractionDigits: 3 })}`}
          primaryTypographyProps={{
            fontSize: '13px',
          }}
          secondaryTypographyProps={{
            fontSize: '11px',
          }}
        />
      </MenuItem>
    </div>
  );
};

const EditableOptionLeg = ({
  leg,
  legGreeks,
  greeksLoading = false,
  onEditLeg,
  expirationOptions,
  disabledExpirationOptions,
  strikeDeltaOptions,
  onDeleteLeg,
}: EditableOptionLegProps) => {
  const theme = useTheme();
  const [localQuantity, setLocalQuantity] = useState<number | null>(
    Math.abs(leg.quantity),
  );
  const [localStrike, setLocalStrike] = useState<number>(leg.strike);
  const [localPrice, setLocalPrice] = useState<number | null>(leg.price_paid);
  const [expirySelectorOpen, setExpirySelectorOpen] = useState(false);
  const [strikeSelectorOpen, setStrikeSelectorOpen] = useState(false);
  const [localExpiration, setLocalExpiration] = useState<number>(leg.expiry);
  const [expirationError, setExpirationError] = useState<string | null>(null);
  const strikeListRef = useRef<List>(null);

  const strikeOptions = useMemo(() => {
    return strikeDeltaOptions.map((object) => object.strike);
  }, [strikeDeltaOptions]);

  // Update local state when props change
  useEffect(() => {
    setLocalPrice(leg.price_paid);
    setLocalStrike(leg.strike);
  }, [leg.price_paid, leg.strike]);

  // Debounced handlers
  const debouncedEditLeg = useCallback(
    debounce(
      (
        legId: number,
        updatedLeg: Partial<PositionLeg>,
        shouldUseDefaultPrice?: boolean,
      ) => {
        onEditLeg(legId, updatedLeg, shouldUseDefaultPrice);
      },
      300,
    ),
    [onEditLeg],
  );

  const handleTypeChange = (newType: 'C' | 'P') => {
    onEditLeg(leg.leg_id, { option_type: newType }, true);
  };

  const handleStrikeOpen = () => {
    setStrikeSelectorOpen(true);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (strikeListRef.current == null || localStrike == null) {
          return;
        }
        const selectedIndex = strikeOptions.indexOf(localStrike);
        if (selectedIndex !== -1) {
          strikeListRef.current.scrollToItem(selectedIndex, 'center');
        }
      });
    });
  };

  const handleStrikeClose = () => {
    setStrikeSelectorOpen(false);
  };

  const handleOpen = () => {
    setExpirySelectorOpen(true);
  };

  const handleClose = () => {
    setExpirySelectorOpen(false);
  };

  const handleExpirationChange = (event: SelectChangeEvent<number>) => {
    const newExpiration = Number(event.target.value);

    // Validate the date
    if (!newExpiration) {
      setExpirationError('Date is required');
      return;
    }

    const dateObj = dayjs(newExpiration);
    const today = dayjs();

    if (!dateObj.isValid()) {
      setExpirationError('Invalid date format');
      return;
    }

    // Update local state with timestamp
    setLocalExpiration(newExpiration);

    if (dateObj.isBefore(today, 'day')) {
      setExpirationError('Date cannot be in the past');
      return;
    }

    // Check if it's a valid market day (simplified check - could be enhanced)
    const isWeekend = dateObj.day() === 0 || dateObj.day() === 6;
    setExpirationError(isWeekend ? 'Warning: Weekend date selected' : null);

    // Only update if date is valid
    if (dateObj.isValid() && !dateObj.isBefore(today, 'day')) {
      debouncedEditLeg(leg.leg_id, { expiry: newExpiration }, true);
    }
  };

  const handleStrikeSelect = (strike: number) => {
    setLocalStrike(strike);
    debouncedEditLeg(leg.leg_id, { strike }, true);
    setStrikeSelectorOpen(false);
  };

  const updatePrice = (newPrice: number) => {
    setLocalPrice(newPrice);
    debouncedEditLeg(leg.leg_id, { price_paid: newPrice });
  };

  const handleResetPrice = () => {
    if (legGreeks == null) {
      return;
    }
    const newPrice = Math.round(legGreeks.price * 1_000) / 1_000;
    updatePrice(newPrice);
  };

  const updateQuantity = (quantity: number) => {
    setLocalQuantity(Math.abs(quantity));
    debouncedEditLeg(leg.leg_id, { quantity }, true);
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '') {
      setLocalPrice(null);
    } else {
      const newPrice = parseFloat(value);
      if (!isNaN(newPrice)) {
        updatePrice(newPrice);
      }
    }
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '') {
      setLocalQuantity(null);
    } else {
      const newQuantity = parseInt(value);
      if (!isNaN(newQuantity)) {
        // Maintain sign of the leg quantity
        updateQuantity(newQuantity * (leg.quantity >= 0 ? 1 : -1));
      }
    }
  };

  const shouldRenderExpirationSelect = expirationOptions.length > 0;
  const shouldRenderStrikeSelect = strikeDeltaOptions.length > 0;

  // Calculate item height and list height for virtualization
  const ITEM_HEIGHT = 50; // Increased height to accommodate delta display
  const MAX_VISIBLE_ITEMS = 6;
  const listHeight = Math.min(
    strikeDeltaOptions.length * ITEM_HEIGHT,
    MAX_VISIBLE_ITEMS * ITEM_HEIGHT,
  );

  const strikeItemData = useMemo(
    () => ({
      strikeDeltaOptions: strikeDeltaOptions,
      selectedStrike: localStrike,
      isCall: leg.option_type === 'C',
      isBuy: leg.quantity >= 0,
      onStrikeSelect: handleStrikeSelect,
    }),
    [
      strikeDeltaOptions,
      localStrike,
      handleStrikeSelect,
      leg.option_type,
      leg.quantity,
    ],
  );

  const hasAdjustedIv =
    legGreeks?.adjustedIv &&
    Math.abs(legGreeks.adjustedIv - legGreeks.iv) > 0.001;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: { xs: 'stretch', lg: 'center' },
        py: { xs: 3, lg: 4 },
        px: { xs: 2, lg: 3 },
        gap: { xs: '16px', lg: '24px' },
      }}
    >
      <Box
        sx={{
          mt: { xs: 3, lg: 0 },
          mr: { xs: 0, lg: 3 },
          display: 'flex',
          justifyContent: { xs: 'center', lg: 'flex-start' },
          mb: { xs: 1, lg: 0 },
        }}
      >
        <BuySellBtn
          onClick={() => updateQuantity(0 - leg.quantity)}
          isBuy={leg.quantity >= 0}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: { xs: 'space-between', lg: 'flex-start' },
        }}
      >
        <FormControl size="small" sx={{ width: { xs: '45%', lg: 80 } }}>
          <InputLabel id="quantity-input-label">Quantity</InputLabel>
          <OutlinedInput
            id="quantity-input"
            value={localQuantity}
            onChange={handleQuantityChange}
            endAdornment={<InputAdornment position="end">x</InputAdornment>}
            label="Quantity"
            type="number"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '12px',
                MozAppearance: 'textfield',
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              svg: {
                color: alpha(theme.palette.primary.main, 0.5),
              },
            }}
          />
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleTypeChange(leg.option_type === 'C' ? 'P' : 'C')}
          color={leg.option_type === 'C' ? 'success' : 'error'}
          sx={{
            minWidth: '40px',
            width: { xs: '45%', lg: 'auto' },
          }}
        >
          {leg.option_type}
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: { xs: 4, md: 3 },
          width: '100%',
        }}
      >
        {shouldRenderStrikeSelect && (
          <FormControl size="small" sx={{ width: { xs: '100%', lg: 120 } }}>
            <InputLabel id="strike-select-label">Strike</InputLabel>
            <Select
              labelId="strike-select-label"
              id="strike-select"
              open={strikeSelectorOpen}
              onClose={handleStrikeClose}
              onOpen={handleStrikeOpen}
              value={localStrike || ''}
              size="small"
              input={
                <OutlinedInput
                  label="Strike"
                  sx={{
                    svg: {
                      color: alpha(theme.palette.primary.main, 0.5),
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '12px',
                    },
                  }}
                />
              }
              renderValue={(selected: number | string) => {
                if (selected === '' || selected == null) {
                  return 'Select Strike';
                }
                return `${formatAsCurrency(Number(selected))}`;
              }}
              MenuProps={{
                sx: {
                  zIndex: 10_001,
                  '& .MuiSelect-icon': {
                    color: theme.palette.text.primary,
                  },
                },
                PaperProps: {
                  style: {
                    width: '180px', // Increased width to accommodate delta display
                    maxHeight: `${listHeight + 16}px`,
                    padding: 0,
                  },
                },
              }}
            >
              <Box sx={{ height: listHeight, width: '100%' }}>
                <List
                  ref={strikeListRef}
                  height={listHeight}
                  itemCount={strikeDeltaOptions.length}
                  itemSize={ITEM_HEIGHT}
                  itemData={strikeItemData}
                  width="100%"
                >
                  {StrikeItem}
                </List>
              </Box>
            </Select>
          </FormControl>
        )}

        {shouldRenderExpirationSelect && (
          <FormControl
            size="small"
            sx={{ width: { xs: '100%', lg: 140 } }}
            error={expirationError != null}
          >
            <InputLabel id="expiry-date-select-label">Expiration</InputLabel>
            <Select
              labelId="expiry-date-select-label"
              id="expiry-date-select"
              open={expirySelectorOpen}
              onClose={handleClose}
              onOpen={handleOpen}
              onChange={handleExpirationChange}
              value={localExpiration}
              size="small"
              input={
                <OutlinedInput
                  label="Expiration"
                  sx={{
                    svg: {
                      color: alpha(theme.palette.primary.main, 0.5),
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '12px',
                    },
                  }}
                />
              }
              renderValue={(selected: number) =>
                `${dayjs(selected).format('YYYY-MM-DD')}`
              }
              MenuProps={{
                sx: {
                  zIndex: 10_001,
                  '& .MuiSelect-icon': {
                    color: theme.palette.text.primary,
                  },
                },
                PaperProps: {
                  style: {
                    width: '140px',
                    maxHeight: '250px',
                  },
                },
              }}
            >
              {expirationOptions.map((utcTime: number) => {
                return (
                  <MenuItem
                    key={utcTime}
                    value={utcTime}
                    sx={{
                      padding: '10px',
                    }}
                    disabled={disabledExpirationOptions.includes(utcTime)}
                  >
                    <Checkbox
                      checked={leg.expiry === utcTime}
                      style={{
                        padding: '0px 10px 0px 0px',
                      }}
                    />
                    <ListItemText
                      primary={`${getExpirationLabel(utcTime.toString())}`}
                      secondary={`${convertToDaysTillExpiry(
                        dayjs().valueOf(),
                        utcTime,
                      )} DTE`}
                      primaryTypographyProps={{
                        fontSize: '13px',
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ width: { xs: '100%', lg: 130 } }}>
          <InputLabel id="price-label">Price</InputLabel>
          <OutlinedInput
            id="price-input"
            value={localPrice}
            onChange={handlePriceChange}
            type="number"
            inputProps={{ step: 0.01 }}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            endAdornment={
              legGreeks?.price && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleResetPrice}
                    sx={{
                      padding: '2px',
                    }}
                    color="primary"
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }
            label="Price"
            sx={{
              '& fieldset': {
                borderColor: alpha(theme.palette.primary.main, 0.35),
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '& input': {
                MozAppearance: 'textfield',
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
            }}
          />
        </FormControl>
      </Box>

      {/* Greeks Display */}
      {greeksLoading ? (
        <Box
          sx={{
            gap: 2,
            width: '100%',
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            p: 2,
            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: `repeat(5, 1fr)`,
              },
              gap: {
                xs: 2,
                sm: 2.5,
                md: 3,
              },
            }}
          >
            {Array.from({ length: 5 }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.background.default, 0.3),
                }}
              >
                <Skeleton
                  variant="text"
                  width={60}
                  height={16}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }}
                />
                <Skeleton
                  variant="text"
                  width={45}
                  height={20}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      ) : legGreeks ? (
        <Box
          sx={{
            gap: 2,
            width: '100%',
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            p: 2,
            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                xl: `repeat(5, 1fr)`,
              },
              gap: {
                xs: 2,
                sm: 2.5,
                md: 3,
              },
            }}
          >
            <GreekBox
              label="IV"
              description="Implied volatility (IV) measures the market's expectation of how much an asset's price will fluctuate in the future, calculated from option prices."
              value={legGreeks.iv}
              decimals={3}
              format="percent"
              useColorCoding={false}
              adjustedValue={hasAdjustedIv ? legGreeks.adjustedIv : undefined}
              sx={{
                minWidth: hasAdjustedIv ? '125px' : '105px',
              }}
            />

            <GreekBox
              label="Gamma (Γ)"
              description="Second-order Greek which dynamically shows the rate of change of delta (directional risk) compared to the rate of change in the underlying security."
              value={legGreeks.gamma}
              decimals={3}
              useColorCoding={false}
            />

            <GreekBox
              label="Vega (𝜈)"
              description="First-order Greek which measures the sensitivity of an option’s price to changes in implied volatility"
              value={legGreeks.vega}
              decimals={3}
              useColorCoding={false}
            />

            <GreekBox
              label="Delta (Δ)"
              description="First-order Greek which measures the sensitivity of an option's price to movement in the underlying security."
              value={legGreeks.delta * (leg.quantity >= 0 ? 1 : -1)}
              decimals={3}
              useColorCoding
            />

            <GreekBox
              label="Theta (Θ)"
              description="First-order Greek which measures how much an option's price changes relative to time."
              value={legGreeks.theta * (leg.quantity >= 0 ? 1 : -1)}
              decimals={3}
              useColorCoding
            />
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          ml: { xs: 0, lg: 'auto' },
          mt: { xs: 2, lg: 0 },
          display: 'flex',
          justifyContent: { xs: 'flex-end', lg: 'flex-start' },
        }}
      >
        <IconButton
          color="error"
          onClick={() => onDeleteLeg(leg.leg_id)}
          sx={{ minWidth: 'auto' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default EditableOptionLeg;
